import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Lock, AlertTriangle, Clock, Zap, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const URGENCY_OPTIONS = [
  { value: 'low', label: 'Low', description: 'No rush - whenever convenient', icon: Clock, color: 'text-gray-500' },
  { value: 'normal', label: 'Normal', description: 'Standard processing time', icon: Clock, color: 'text-[#4A3B32]' },
  { value: 'high', label: 'High', description: 'Needed within 24 hours', icon: AlertTriangle, color: 'text-amber-500' },
  { value: 'urgent', label: 'Urgent', description: 'Needed immediately', icon: Zap, color: 'text-red-500' },
];

export default function AccessRequestModal({ 
  isOpen, 
  onClose, 
  client,
  onSuccess 
}) {
  const { token } = useAuth();
  const [reason, setReason] = useState('');
  const [urgency, setUrgency] = useState('normal');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!reason.trim()) {
      toast.error('Please provide a reason for your access request');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.createAccessRequest(token, {
        clientId: client.id,
        reason: reason.trim(),
        urgency
      });
      
      setSubmitted(true);
      toast.success('Access request submitted successfully');
      
      // Delay close to show success state
      setTimeout(() => {
        onClose();
        onSuccess?.();
        // Reset state
        setReason('');
        setUrgency('normal');
        setSubmitted(false);
      }, 1500);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to submit access request';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setReason('');
      setUrgency('normal');
      setSubmitted(false);
      onClose();
    }
  };

  if (submitted) {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-md">
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-[#2C2420] mb-2">Request Submitted</h3>
            <p className="text-[#5D524D]">
              Your access request has been sent to the administrators for review.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#7D8C9E]" />
            Request Access
          </DialogTitle>
          <DialogDescription>
            Request permission to view the restricted client profile.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Client Info */}
          <div className="p-4 bg-[#F9F7F5] rounded-lg border border-[#E6E0D9]">
            <p className="text-sm text-[#8C8078] mb-1">Requesting access to</p>
            <p className="font-semibold text-[#2C2420]">{client?.name}</p>
            <p className="text-xs text-[#7D8C9E] mt-2 flex items-center gap-1">
              <Lock className="w-3 h-3" />
              Restricted access applies
            </p>
          </div>

          {/* Reason */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="text-[#2C2420]">
              Reason for Access <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="reason"
              placeholder="Please explain why you need access to this client's information..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="min-h-[100px] resize-none"
              data-testid="access-request-reason"
            />
            <p className="text-xs text-[#8C8078]">
              This will be reviewed by an administrator.
            </p>
          </div>

          {/* Urgency */}
          <div className="space-y-2">
            <Label className="text-[#2C2420]">Urgency Level</Label>
            <div className="grid grid-cols-2 gap-2">
              {URGENCY_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = urgency === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setUrgency(option.value)}
                    className={`p-3 rounded-lg border text-left transition-all ${
                      isSelected
                        ? 'border-[#4A3B32] bg-[#4A3B32]/5 ring-1 ring-[#4A3B32]'
                        : 'border-[#E6E0D9] hover:border-[#C6A88F] hover:bg-[#F9F7F5]'
                    }`}
                    data-testid={`urgency-${option.value}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-4 h-4 ${option.color}`} />
                      <span className={`font-medium text-sm ${isSelected ? 'text-[#2C2420]' : 'text-[#5D524D]'}`}>
                        {option.label}
                      </span>
                    </div>
                    <p className="text-xs text-[#8C8078]">{option.description}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#E6E0D9]">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
              data-testid="cancel-access-request"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !reason.trim()}
              className="bg-[#4A3B32] hover:bg-[#3A2B22]"
              data-testid="submit-access-request"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Request'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
